// src/pages/VerifiedPage.jsx
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  verifyDriverEmail,
  selectVerifyStatus,
  selectVerifyError,
  selectVerifiedDriver,
} from "../Paneldriver/DriverSlice/Driverslice";
import { saveDriverProfileForced, getDriverProfile } from "../Paneldriver/DriverSlice/Drivercookie";
import { formatGridCode } from "../Paneldriver/DriverSlice/Gridcode";
import "../styles/Verifiedpage.css";

const SESSION_KEY = "vk_verify_token";

export default function VerifiedPage() {
  const dispatch       = useDispatch();
  const verifyStatus   = useSelector(selectVerifyStatus);
  const verifyError    = useSelector(selectVerifyError);
  const verifiedDriver = useSelector(selectVerifiedDriver);

  useEffect(() => {
    if (verifyStatus !== "idle") return;
    const params   = new URLSearchParams(window.location.search);
    const rawParam = params.get("t");
    if (rawParam) {
      let token = rawParam;
      try { token = atob(rawParam.replace(/-/g,"+").replace(/_/g,"/")); } catch { token = rawParam; }
      sessionStorage.setItem(SESSION_KEY, token);
      window.history.replaceState({}, document.title, window.location.pathname);
      dispatch(verifyDriverEmail({ token }));
      return;
    }
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) dispatch(verifyDriverEmail({ token: stored }));
  }, [verifyStatus, dispatch]);

  useEffect(() => {
    if (verifyStatus !== "succeeded") return;
    const existing = getDriverProfile() || {};
    saveDriverProfileForced({
      ...existing,
      registered: true,
      callsign:   verifiedDriver?.callsign || existing.callsign || "",
      gridCode:   existing.gridCode        || verifiedDriver?.serialNumber || null,
      verifiedAt: new Date().toISOString(),
    });
  }, [verifyStatus, verifiedDriver]);

  const profile     = getDriverProfile();
  const displayCode = profile?.gridCode || verifiedDriver?.serialNumber || null;

  const hasToken  = Boolean(new URLSearchParams(window.location.search).get("t")) || Boolean(sessionStorage.getItem(SESSION_KEY));
  const isLoading = hasToken && (verifyStatus === "idle" || verifyStatus === "loading");
  const isSuccess = verifyStatus === "succeeded";
  const isError   = verifyStatus === "failed";
  const noToken   = !hasToken && verifyStatus === "idle";

  return (
    <div className="vkv-shell">
      {isLoading && (
        <div className="vkv-card">
          <div className="vkv-spinner"/>
          <div className="vkv-eyebrow">// VERIFYING IDENTITY</div>
          <div className="vkv-title">CONFIRMING<br/><span className="orange">DRIVER PROFILE</span></div>
          <p className="vkv-sub">Authenticating token — stand by...</p>
        </div>
      )}
      {isSuccess && (
        <div className="vkv-card">
          <div className="vkv-check">✓</div>
          <div className="vkv-eyebrow orange">// REGISTRATION CONFIRMED</div>
          <div className="vkv-title">WELCOME TO<br/><span className="orange">THE GRID</span></div>
          {verifiedDriver?.callsign && <div className="vkv-callsign">// {verifiedDriver.callsign}</div>}
          <p className="vkv-sub">Your registration is confirmed. Mission briefings and Winter 2026 deployment updates will arrive at your inbox.</p>
          {verifiedDriver && (
            <div className="vkv-driver-card">
              <div className="vkv-dc-header">
                <span className="vkv-dc-logo">VK</span>
                <span className="vkv-dc-badge">DRIVER PROFILE</span>
                <span className="vkv-dc-status">ACTIVE</span>
              </div>
              <div className="vkv-dc-callsign">
                {(verifiedDriver.callsign || verifiedDriver.name || "DRIVER").toUpperCase()}
              </div>
              <div className="vkv-dc-rows">
                {[["STATUS","ACTIVE","lime"],["DEPLOYMENT","WINTER 2026",""]].map(([l,v,c]) => (
                  <div key={l} className="vkv-dc-row">
                    <span className="vkv-dc-label">{l}</span>
                    <span className={`vkv-dc-val${c?" "+c:""}`}>{v}</span>
                  </div>
                ))}
                {displayCode && (
                  <div className="vkv-dc-row">
                    <span className="vkv-dc-label">GRID CODE</span>
                    <span className="vkv-dc-val blue">{formatGridCode(displayCode)}</span>
                  </div>
                )}
              </div>
              <div className="vkv-dc-footer">VEGAS KARTS LLC · LAS VEGAS, NV</div>
            </div>
          )}
          <a href="/" className="vkv-btn">BACK TO BASE ▶</a>
        </div>
      )}
      {isError && (
        <div className="vkv-card vkv-card--error">
          <div className="vkv-error-icon">✕</div>
          <div className="vkv-eyebrow" style={{color:"#FF3800"}}>// VERIFICATION FAILED</div>
          <div className="vkv-title">LINK EXPIRED<br/>OR INVALID</div>
          <p className="vkv-sub">{verifyError || "This verification link has expired or already been used."}</p>
          <p className="vkv-sub" style={{marginTop:"0.5rem"}}>Return to the driver registry to re-register and receive a new link.</p>
          <a href="/" className="vkv-btn vkv-btn--ghost">BACK TO REGISTRY ▶</a>
        </div>
      )}
      {noToken && (
        <div className="vkv-card vkv-card--error">
          <div className="vkv-error-icon">⚠</div>
          <div className="vkv-title">NO TOKEN<br/>PROVIDED</div>
          <p className="vkv-sub">Use the link from your registration email.</p>
          <a href="/" className="vkv-btn vkv-btn--ghost">BACK TO HOME ▶</a>
        </div>
      )}
    </div>
  );
}